import { HeadContent, Scripts, createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { studentWorkspaceApi } from '@/lib/api/student-workspace'
import type { WorkspaceConfig } from '@/lib/workspace-context'

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
    import('@tanstack/react-query-devtools').then((mod) => ({
      default: mod.ReactQueryDevtools,
    })),
  )
  : () => null

import appCss from '@/styles.css?url'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async (): Promise<{ workspace: WorkspaceConfig | null }> => {
    const { data } = await studentWorkspaceApi().api.student['workspace-config'].get()
    return { workspace: (data as WorkspaceConfig) ?? null }
  },

  head: ({ loaderData }) => {
    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { title: (loaderData?.workspace?.business as any)?.businessName ?? 'Área do Aluno' },
      ],
      links: [
        { rel: 'stylesheet', href: appCss },
        { rel: 'stylesheet', href: '/theme.css' },
        ...((loaderData?.workspace?.business as any)?.logoUrl
          ? [{ rel: 'icon', href: (loaderData?.workspace!.business as any).logoUrl }]
          : []),
      ],
    }
  },

  component: RootComponent,
  notFoundComponent: NotFoundPage,
  shellComponent: RootDocument,
})

function RootComponent() {
  return <Outlet />
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <h1 className="text-6xl font-bold text-muted-foreground">404</h1>
        <h2 className="text-2xl font-semibold text-foreground">
          Página não encontrada
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          A página que você está procurando não existe ou foi movida.
        </p>
        <div className="pt-4">
          <a
            href="/"
            className="inline-block px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-colors"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    </div>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {


  return (
    <html lang="pt-BR" className="dark" suppressHydrationWarning>
      <head>
        <HeadContent />
        <style>{`html,body{background:oklch(0.145 0.005 285);color:oklch(0.95 0.005 285);color-scheme:dark}`}</style>
      </head>
      {/*
        🔴 SEM `<QueryClientProvider>` À MÃO — quem envolve é o
        `setupRouterSsrQueryIntegration` (`router.tsx`), com o client DAQUELE
        router.

        O provider daqui recebia um `queryClient` de MÓDULO, e módulo no
        servidor é do PROCESSO: um cliente só para todos os alunos que o
        container atende. É o mesmo motivo pelo qual os outros cinco apps criam
        o client dentro do `getRouter()`.

        Quem monta o provider agora é `router.options.Wrap`, que a integração
        instala quando `wrapQueryClient` fica no default (`true`). O
        `wrapQueryClient: false` de lá existia só para não haver DOIS providers.
      */}
      <body>
        {children}
        <Toaster position="bottom-right" richColors theme="dark" />
        {import.meta.env.DEV && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
        <Scripts />
      </body>
    </html>
  )
}